import React, { useLayoutEffect, useRef, useState } from 'react';

export default function EventDescription({
  description = '',
  event = {},
  mode,
  purpose,
}) {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const paragraphRef = useRef(null);

  // Collapse when the description changes.
  useLayoutEffect(() => {
    setExpanded(false);
  }, [description]);

  useLayoutEffect(() => {
    const paragraph = paragraphRef.current;
    if (!paragraph || expanded) return undefined;

    let active = true;

    const checkTruncation = () => {
      if (!active) return;

      setIsTruncated(
        paragraph.scrollHeight > paragraph.clientHeight + 1
      );
    };

    checkTruncation();

    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(checkTruncation)
        : null;

    observer?.observe(paragraph);
    window.addEventListener('resize', checkTruncation);

    // Recheck after fonts load, since they can change text wrapping.
    document.fonts?.ready.then(checkTruncation);

    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener('resize', checkTruncation);
    };
  }, [description, expanded]);

  return (
    <>
      <div style={S.hairlineTop} />

      <h3 style={S.h3}>What to expect</h3>

      <p
        ref={paragraphRef}
        style={expanded ? { ...S.copy, ...S.copyExpanded } : S.copy}
      >
        {description}
      </p>

      {(isTruncated || expanded) && (
        <div style={S.moreWrap}>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
            style={{
              ...S.moreText,
              border: 0,
              padding: 0,
              background: 'transparent',
            }}
          >
            {expanded ? 'Read less' : 'Read more'}
          </button>
        </div>
      )}

      <div style={S.hairline} />
    </>
  );
}

const S = {
  h3: {
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    margin: '20px 16px 8px',
    fontSize: 12,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#000',
  },

  // Default = 3-line clamp
  copy: {
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    margin: '0 16px 8px',
    fontSize: 13,
    lineHeight: 1.6,
    color: '#374151',
    whiteSpace: 'pre-line',

    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },

  // When expanded → remove clamp
  copyExpanded: {
    display: 'block',
    WebkitLineClamp: 'unset',
    WebkitBoxOrient: 'unset',
    overflow: 'visible',
  },

  // Align the text link with the paragraph margins
  moreWrap: {
    display: 'block',
    margin: '0 16px 20px',
  },

  hairlineTop: {
    height: 1,
    background: '#EEF0F2',
    margin: '20px 16px 16px',
  },

  hairline: {
    height: 1,
    background: '#EEF0F2',
    margin: '8px 16px 18px',
  },

  // Plain text-style control
  moreText: {
    cursor: 'pointer',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    color: '#0B0B0B',
    textDecoration: 'none',
    outline: 'none',
  },
};